import type { IMsalContext } from '@azure/msal-react';
import { QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, Outlet, redirect } from '@tanstack/react-router';

import { DocumentTitle } from '@/components/layout/document-title';
import { toast } from '@/components/ui/toast';
import { AUTH_MODE, AuthMode } from '@/config/env';
import { Language, SUPPORTED_LANGUAGES } from '@/config/locale';
import { isLoggedInForDemo } from '@/config/msal';
import { isLoggedIn, saveTokens } from '@/lib/auth-token';
import i18n from '@/lib/i18n';
import { loginWithSecuredId } from '@/mocked_api/auth';

export const Route = createRootRouteWithContext<{
	queryClient: QueryClient;
	msal?: IMsalContext;
}>()({
	beforeLoad: async ({ location, search, context: { msal } }) => {
		if (location.pathname !== '/') {
			return;
		}

		if (msal && isLoggedInForDemo(msal.instance)) {
			throw redirect({ to: '/dashboard', throw: true });
		}

		const searchParams = search as { securedId?: string; lang?: string };

		if (searchParams.lang && SUPPORTED_LANGUAGES.includes(searchParams.lang as Language)) {
			await i18n.changeLanguage(searchParams.lang);
		}

		if (AUTH_MODE === AuthMode.Real && searchParams.securedId) {
			// Strip the token from history immediately — before any async work — so it
			// can't be bookmarked, shared, or re-submitted via back-navigation.
			history.replaceState(null, '', location.pathname);

			let loginError: Error | null = null;

			try {
				const response = await loginWithSecuredId(searchParams.securedId);

				saveTokens({
					accessToken: response.accessToken,
					accessTokenExpiration: response.accessTokenExpiration,
					refreshToken: response.refreshToken,
					refreshTokenExpiration: response.refreshTokenExpiration,
				});
			} catch (error) {
				if (error instanceof Error) {
					loginError = error;
				} else {
					throw error;
				}
			}

			if (loginError) {
				console.error('Login failed:', loginError);
				toast.error(i18n.t('auth.toast.loginFailed'));
				throw redirect({ to: '/sign-in', throw: true });
			}

			if (!isLoggedIn()) {
				toast.error(i18n.t('auth.toast.tokenSaveFailed'));
				throw redirect({ to: '/sign-in', throw: true });
			}

			toast.success(i18n.t('auth.toast.loginSuccess'));
			throw redirect({ to: '/dashboard', search: {}, throw: true });
		}

		throw redirect({ to: '/sign-in', throw: true });
	},
	component: () => (
		<>
			<DocumentTitle />
			<Outlet />
		</>
	),
});
